import { z } from 'zod';
import {
  ElementNodeSchema, MAX_ELEMENT_DEPTH, MAX_ELEMENT_NODES,
  type ElementNodeDto, type ElementTreeDto
} from './element.schema';

// A section the merchant saved to use again, and the one answer they gave when they saved it: a copy they
// can drop on any page, or a live link that keeps every placement of it in step. The choice is made once,
// at save time, in two sentences — everything else in this file follows from that single field.
//
// A live-linked section is stored here once and a page carries a stub pointing at it, so editing it on any
// page edits it on all of them and nine pages holding the same header cost one body. A copy is a template
// and nothing more: inserting it mints an ordinary local subtree that forgets where it came from.
//
// The library rides in `store_themes.config` beside the page trees. That is what makes a shared body
// impossible to read across tenants and impossible to leave stale against the tree pointing at it: the
// two are written on the same row and dropped by the one cache-key list that config already has. It is
// optional in the same spirit as `trees` — a store that has saved nothing carries no key here and reads
// exactly as it did before this existed.

/** Which of the two radios the merchant chose, and so what inserting the section does. */
export const SHARED_MODES = ['copy', 'link'] as const;
export const SharedModeEnum = z.enum(SHARED_MODES);

/** How the saved-sections list is grouped; the same closed vocabulary the Add panel draws its headers from. */
export const SHARED_CATEGORIES = [
  'header', 'footer', 'hero', 'banners', 'call-to-action', 'features', 'benefits', 'how-it-works',
  'faq', 'pricing', 'bundles', 'product', 'product-list', 'collection', 'cart', 'checkout',
  'reviews', 'testimonials', 'badges', 'stats', 'content', 'gallery', 'video',
  'contact', 'sign-up', 'thank-you', 'about-us', 'other'
] as const;
export const SharedCategoryEnum = z.enum(SHARED_CATEGORIES);

/** The most saved sections one store may hold, chosen to equal the largest page a list read may answer with. */
export const MAX_SHARED_SECTIONS = 100;

/**
 * The most the whole library may weigh. One body can never arrive larger than the 1 MB an API body may be,
 * and the library is read on the same row as the trees on every merged-theme cache miss — so the two caps
 * together are what keep that row bounded no matter how many sections a merchant saves one at a time.
 */
export const MAX_SHARED_BYTES = 1_048_576;

/** An entry's id, in the same grammar an element id uses, because a stub carries one in `sharedId`. */
export const SharedIdSchema = z.string().regex(
  /^[a-zA-Z0-9_-]{1,64}$/,
  'A saved section id is letters, digits, dashes and underscores'
);

/** One saved section: what the merchant called it, where it is filed, which radio they chose, and its body. */
export const SharedSectionSchema = z.object({
  name: z.string().trim().min(1).max(80),
  category: SharedCategoryEnum,
  mode: SharedModeEnum,
  /** The section itself. Its ids are the library's own — `b1`, `b2` — never a page's; see `liftShared`. */
  node: ElementNodeSchema,
  updatedAt: z.string().datetime()
}).strict();

/** Every section one store has saved, by the id a stub points at. */
export const SharedLibrarySchema = z.record(SharedIdSchema, SharedSectionSchema);

export type SharedMode = z.infer<typeof SharedModeEnum>;
export type SharedCategory = z.infer<typeof SharedCategoryEnum>;
export type SharedSectionDto = z.infer<typeof SharedSectionSchema>;
export type SharedLibraryDto = z.infer<typeof SharedLibrarySchema>;

/** A library nobody has written to, shared so every read of a store without one hands back one object. */
export const NO_SHARED_LIBRARY: SharedLibraryDto = Object.freeze({});

// Names every saved section one subtree points at, at any depth, so a reference can be counted without a walk per id.
export function sharedRefs(nodes: readonly ElementNodeDto[], into: Set<string> = new Set()): Set<string> {
  for (const node of nodes) {
    if (node.sharedId) into.add(node.sharedId);
    sharedRefs(node.children, into);
  }
  return into;
}

/**
 * Names the chain of saved sections that ends where it began, or null when the library is a plain graph.
 * A section holding itself — directly, or through two others — is a page the renderer would walk forever,
 * so it is refused at the write rather than guarded against on every read.
 */
export function sharedCycle(library: SharedLibraryDto): readonly string[] | null {
  const settled = new Set<string>();
  const open = new Set<string>();
  const trail: string[] = [];

  const walk = (id: string): readonly string[] | null => {
    if (open.has(id)) return [...trail.slice(trail.indexOf(id)), id];
    if (settled.has(id) || !library[id]) return null;
    open.add(id);
    trail.push(id);
    for (const next of sharedRefs([library[id].node])) {
      const found = walk(next);
      if (found) return found;
    }
    open.delete(id);
    trail.pop();
    settled.add(id);
    return null;
  };

  return Object.keys(library).map(walk).find((found) => found !== null) ?? null;
}

// Reports whether every reference inside a library body names a live-linked entry the same library holds.
function refsResolve(library: SharedLibraryDto): boolean {
  const links = new Set(Object.keys(library).filter((id) => library[id].mode === 'link'));
  return Object.keys(library).every((id) => [...sharedRefs([library[id].node])].every((ref) => links.has(ref)));
}

/**
 * Reads a stored library, refusing one that is too large, one whose body claims to be a saved section in
 * its own right, one naming an entry that is not there, and one that loops. `null` means the same thing it
 * means for a page tree: this is not a library, and the caller says why.
 */
export function readSharedLibrary(stored: unknown): SharedLibraryDto | null {
  const parsed = SharedLibrarySchema.safeParse(stored ?? {});
  if (!parsed.success) return null;
  const library = parsed.data;
  const ids = Object.keys(library);
  if (ids.length > MAX_SHARED_SECTIONS) return null;
  // Measured after parsing, because a prop value is `unknown` and no per-field rule can bound one.
  if (JSON.stringify(library).length > MAX_SHARED_BYTES) return null;
  if (ids.some((id) => library[id].node.sharedId !== undefined)) return null;
  if (!refsResolve(library)) return null;
  return sharedCycle(library) === null ? library : null;
}

/** Where one drawn node came from: the entry whose body holds it, and the node it is inside that body. */
export type SharedOrigin = { sharedId: string; bodyId: string };

/**
 * A page with its live-linked bodies drawn in. `origins` is what routes an edit made on the page back to
 * the library instead of the page, and `truncated` says a link was left as a stub because drawing it would
 * have taken the page past what the renderer walks — which is a write to refuse, not a page to serve.
 */
export type SharedResolution = {
  tree: ElementTreeDto;
  origins: ReadonlyMap<string, SharedOrigin>;
  truncated: boolean;
};

/** One step of the walk: the instance a node is being drawn inside, and the entries already open above it. */
type ResolveAt = { prefix: string | null; sharedId: string | null; open: ReadonlySet<string>; depth: number };

/** What the whole walk shares: the bodies, where each drawn node came from, and how many nodes are left. */
type ResolveRun = { library: SharedLibraryDto; origins: Map<string, SharedOrigin>; budget: number; truncated: boolean };

/** The page's own root, before any body has been drawn into it. */
const PAGE_ROOT: ResolveAt = { prefix: null, sharedId: null, open: new Set(), depth: 1 };

/**
 * Names one node as the page actually draws it. A body's own ids are the library's, so two placements of
 * the same section on one page would name the same node twice; prefixing with the stub's id — which is
 * unique to the page — keeps every drawn node distinct without the library knowing where it was placed.
 */
const drawnId = (id: string, prefix: string | null): string => (prefix === null ? id : `${prefix}-${id}`);

// Reports whether one node's link may be drawn here: the entry is live, not already open above, and there is room.
function drawable(node: ElementNodeDto, at: ResolveAt, run: ResolveRun): boolean {
  if (!node.sharedId || at.open.has(node.sharedId)) return false;
  const entry = run.library[node.sharedId];
  if (!entry || entry.mode !== 'link') return false;
  return run.budget > 0 && at.depth < MAX_ELEMENT_DEPTH;
}

// Draws one node, and the body behind it when it is a live link the page still has room for.
function resolveNode(node: ElementNodeDto, at: ResolveAt, run: ResolveRun): ElementNodeDto {
  const id = drawnId(node.id, at.prefix);
  run.budget -= 1;
  if (at.sharedId) run.origins.set(id, { sharedId: at.sharedId, bodyId: node.id });

  if (!drawable(node, at, run)) {
    if (node.sharedId && run.library[node.sharedId]) run.truncated = true;
    return { ...node, id, children: resolveNodes(node.children, { ...at, depth: at.depth + 1 }, run) };
  }

  const sharedId = node.sharedId as string;
  const body = run.library[sharedId].node;
  const inside: ResolveAt = { prefix: id, sharedId, open: new Set([...at.open, sharedId]), depth: at.depth + 1 };
  run.origins.set(id, { sharedId, bodyId: body.id });
  return { ...body, id, sharedId, children: resolveNodes(body.children, inside, run) };
}

// Draws one list of siblings, which is the only way this walk ever recurses.
function resolveNodes(nodes: readonly ElementNodeDto[], at: ResolveAt, run: ResolveRun): ElementNodeDto[] {
  return nodes.map((node) => resolveNode(node, at, run));
}

/**
 * Draws a page's live-linked bodies into it. The result is what a shopper is sent and what the builder
 * shows: a plain tree, with nothing left for the renderer to look up. It never throws and never loops —
 * a stub naming an entry that is not there stays a stub, and one naming an entry already open above it
 * stays a stub too, so no stored row can hang a storefront.
 */
export function resolveShared(tree: ElementTreeDto, library: SharedLibraryDto): SharedResolution {
  const run: ResolveRun = { library, origins: new Map(), budget: MAX_ELEMENT_NODES, truncated: false };
  return { tree: { ...tree, roots: resolveNodes(tree.roots, PAGE_ROOT, run) }, origins: run.origins, truncated: run.truncated };
}

/**
 * Copies one page subtree into a body the library can hold: every id re-minted into the library's own
 * `b1`, `b2` space so a body never carries a page's ids, and the root's own `sharedId` dropped so a
 * section can never be its own reference. Stubs deeper inside are kept — that is a section holding
 * another one, which is allowed as long as it does not come back round.
 */
export function liftShared(node: ElementNodeDto): ElementNodeDto {
  let minted = 0;
  const lift = (source: ElementNodeDto, root: boolean): ElementNodeDto => {
    minted += 1;
    const body: ElementNodeDto = { ...source, id: `b${minted}`, children: source.children.map((child) => lift(child, false)) };
    if (root) delete body.sharedId;
    return body;
  };
  return lift(node, true);
}

/** The stub a page carries where a live-linked section sits: its own id and type, and the entry it points at. */
export const sharedStub = (node: ElementNodeDto, sharedId: string): ElementNodeDto =>
  ({ id: node.id, type: node.type, props: {}, styles: {}, children: [], sharedId });

// Names every entry reachable from a store's pages, following a body's own stubs so a nested one is not lost.
function reachableShared(library: SharedLibraryDto, trees: readonly ElementTreeDto[]): Set<string> {
  const reached = new Set<string>();
  const pending = [...sharedRefs(trees.flatMap((tree) => tree.roots))];
  while (pending.length > 0) {
    const id = pending.pop() as string;
    if (reached.has(id)) continue;
    reached.add(id);
    const entry = library[id];
    if (entry) pending.push(...sharedRefs([entry.node]));
  }
  return reached;
}

/**
 * Drops every live-linked body no page points at any more, which is how a detached or deleted section is
 * collected rather than left behind. A copy is exempt: nothing ever points at one, so counting references
 * would delete the whole Saved-sections list the moment it was written. Copies go when the merchant says so,
 * and `MAX_SHARED_SECTIONS` is what bounds them in the meantime.
 */
export function collectShared(library: SharedLibraryDto, trees: readonly ElementTreeDto[]): SharedLibraryDto {
  const reached = reachableShared(library, trees);
  const kept: SharedLibraryDto = {};
  for (const id of Object.keys(library)) {
    if (library[id].mode === 'copy' || reached.has(id)) kept[id] = library[id];
  }
  return kept;
}
